import * as React from 'react'
import { useContext, useMemo } from 'react'
import { CompoundButton } from '@fluentui/react-components'
import * as UUID from 'uuid'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import { useFormikContext } from 'formik'
import { LegalAidCertificateDto } from './LegalAidCertificateForm'
import { LegalAidCertificateInvoiceProps } from './Invoices'
import { isAppContext } from '../../../helpers/appHelper'
import { AppContext } from '../../../config/appConfig'
import useComposeInvoiceOutlookMessage from '../../../hooks/finance/invoice/useComposeInvoiceOutlookMessage'
import usePersistLegalAidCertificateInvoice from '../../../hooks/finance/invoice/usePersistLegalAidCertificateInvoice'
import { bundleIcon, DocumentQueueAddFilled, DocumentQueueAddRegular } from '@fluentui/react-icons'
import { QueueStatus } from '../../../hooks/useQueueStateHandler'
import useSubmitInvoiceDocumentGeneration from '../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import { DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { PersistInvoiceDto } from '../../../hooks/finance/invoice/usePersistInvoice'

const DocumentQueueIcon = bundleIcon(DocumentQueueAddFilled, DocumentQueueAddRegular)

export default function CreateContributionInvoiceButton(
    props: LegalAidCertificateInvoiceProps & { canCreateInvoice: boolean },
) {
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)
    const { values, setFieldValue, submitForm } = useFormikContext<LegalAidCertificateDto>()
    const requestId = useMemo(() => UUID.v4(), [props.selected?.id, values.contributionInvoiceId])
    const persist = usePersistLegalAidCertificateInvoice(props.file, props.parties)
    const generatePdf = useSubmitInvoiceDocumentGeneration(DocumentGenerationType.Invoice)
    const composeOutlookMessage = useComposeInvoiceOutlookMessage()

    const canCreateContributionInvoice = useMemo<boolean>(
        () => !values.contributionInvoiceId && props.canCreateInvoice,
        [props.canCreateInvoice, values.contributionInvoiceId],
    )
    const createContributionInvoice = () =>
        persist(
            requestId,
            {
                templateId: settings.contributionInvoice.templateId,
                clientPivotId: values.clientPivotId,
                debtorPivotId: values.clientPivotId,
                currency: values.currency,
                recipientReference: values.reference,
            },
            (data: PersistInvoiceDto) => {
                generatePdf(data, {
                    [QueueStatus.Finished]: pdfData => {
                        if (isAppContext([AppContext.Read, AppContext.Module])) {
                            composeOutlookMessage(
                                data.invoiceId,
                                props.parties.data?.find(party => party.pivotId === values.clientPivotId)?.email,
                                pdfData.status.driveItem,
                            )
                        }
                        setFieldValue('contributionInvoiceId', data.invoiceId)
                        submitForm()
                    },
                })
            },
            { [settings.contributionInvoice.feeTemplateId]: values.contribution },
        )

    return (
        <CompoundButton
            disabled={!canCreateContributionInvoice}
            secondaryContent="Verstuur declaratie eigen bijdrage"
            appearance="subtle"
            icon={<DocumentQueueIcon />}
            onClick={createContributionInvoice}
        >
            Eigen bijdrage
        </CompoundButton>
    )
}
